import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { posts } from '@/mocks/community';
import type { Reply } from '@/mocks/community';

export default function PostDetail() {
  const { id } = useParams();
  const post = posts.find((p) => p.id === Number(id));
  const [replies, setReplies] = useState<Reply[]>(post?.repliesList || []);
  const [replyText, setReplyText] = useState('');
  const [sent, setSent] = useState(false);

  if (!post) {
    return (
      <Layout>
        <div className="mx-auto w-full max-w-3xl px-4 pt-32 pb-20 text-center">
          <h1 className="font-heading text-2xl">帖子不存在或已删除</h1>
          <Link
            to="/community"
            className="mt-4 inline-flex whitespace-nowrap items-center gap-1 rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
          >
            返回讨论社区
          </Link>
        </div>
      </Layout>
    );
  }

  const handleReply = () => {
    if (!replyText.trim()) return;
    const next: Reply = {
      id: Date.now(),
      author: '热心同好',
      time: '刚刚',
      text: replyText.trim(),
      likes: 0,
    };
    setReplies((r) => [next, ...r]);
    setReplyText('');
    setSent(true);
    setTimeout(() => setSent(false), 2000);
  };

  return (
    <Layout>
      <div className="mx-auto w-full max-w-4xl px-4 md:px-6 pt-24 md:pt-28 pb-16">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-700">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/community" className="hover:text-primary-700">讨论社区</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">{post.board}</span>
        </nav>

        <article className="mt-6 overflow-hidden rounded-2xl border border-background-200 bg-background-50">
          <div className="border-b border-background-200 p-5 md:p-7">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-0.5 text-xs font-medium text-primary-700">
                <i className="ri-discuss-fill text-xs"></i>{post.board}
              </span>
              {post.pinned && (
                <span className="rounded-full bg-secondary-100 px-3 py-0.5 text-xs text-secondary-700">置顶</span>
              )}
            </div>
            <h1 className="mt-3 text-xl md:text-2xl font-bold leading-snug text-foreground-950">{post.title}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-100 font-heading text-accent-700">
                  {post.author.slice(0, 1)}
                </span>
                <span className="font-medium text-foreground-700">{post.author}</span>
              </span>
              <span>{post.time}</span>
              <span><i className="ri-eye-line mr-1"></i>{post.views.toLocaleString()} 浏览</span>
              <span><i className="ri-chat-3-line mr-1"></i>{post.replies} 回复</span>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {post.tags.map((t) => (
                <span key={t} className="rounded-md bg-background-100 px-2.5 py-1 text-xs text-foreground-600">
                  #{t}
                </span>
              ))}
            </div>
          </div>

          <div className="p-5 md:p-7">
            <div className="space-y-4 text-sm md:text-base leading-relaxed text-foreground-800">
              {post.content.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>
        </article>

        <section className="mt-8">
          <h2 className="flex items-center gap-2 font-heading text-lg md:text-xl">
            <i className="ri-chat-3-fill text-primary-600"></i>
            全部回复（{replies.length}）
          </h2>

          <div className="mt-4 flex flex-col gap-4">
            {replies.map((r) => (
              <div key={r.id} className="flex gap-3 rounded-2xl border border-background-200 bg-background-50 p-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-100 font-heading text-secondary-700">
                  {r.author.slice(0, 1)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                    <span className="font-semibold text-foreground-800">{r.author}</span>
                    <span>{r.time}</span>
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">{r.text}</p>
                  <button
                    type="button"
                    className="mt-2 inline-flex items-center gap-1 text-xs text-foreground-400 hover:text-primary-600 transition-colors"
                  >
                    <i className="ri-heart-line"></i>{r.likes > 0 ? r.likes : '点赞'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-2xl border border-background-200 bg-background-50 p-4 md:p-5">
            <h3 className="text-sm font-bold text-foreground-950">参与讨论</h3>
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="友善交流，理性讨论，说说你的看法…"
              className="mt-3 w-full rounded-lg border border-background-200 bg-background-50 p-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            ></textarea>
            <div className="mt-3 flex items-center justify-between">
              <span className="text-xs text-foreground-400">{replyText.length}/500</span>
              <button
                type="button"
                onClick={handleReply}
                className="inline-flex whitespace-nowrap items-center gap-1.5 rounded-md bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
              >
                <i className="ri-send-plane-fill"></i>发表回复
              </button>
            </div>
            {sent && <p className="mt-2 text-sm text-secondary-700">回复已发布，感谢参与讨论！</p>}
          </div>
        </section>
      </div>
    </Layout>
  );
}